import { requiredPermissions } from '~/utils/permissions'

/** 不需登入即可造訪的 public 路徑。 */
const PUBLIC_PATHS = ['/login', '/forgot-password']

/**
 * 全域路由守衛：
 * - 未登入造訪非 public 頁 → 導回 /login
 * - 已登入造訪 /login → 導向 /
 * - 已登入但 profile 未載入（重整 / 新 tab）→ 補抓 profile，
 *   讓 hasRole() 等 role-based UI 守衛能正確判斷
 *
 * SSR 注意：isLoggedIn 讀 Pinia store，不讀 useCookie。
 * useCookie 在 SSR 只讀 request cookie，clearSession 清完後同一 request 內
 * 後續 middleware 仍會看到 stale request cookie → 觸發 redirect loop（fetchProfile 401
 * → 清 session + 導 /login → /login 看到 request cookie 判 isLoggedIn=true 又導 / → 無限）。
 * Pinia store 是 per-SSR-request singleton，clearSession 後同 request 內讀到 null，
 * 才是「當前 session state」的正確 source of truth。
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const isPublic = PUBLIC_PATHS.includes(to.path)
  const auth = useAuthStore()
  const isLoggedIn = !!auth.token

  if (!isLoggedIn && !isPublic) {
    // 帶上原本要去的頁面，讓 LoginForm 登入成功後可以導回來
    // （例如審核信連結在未登入狀態被點開的情境）
    return navigateTo({ path: '/login', query: { redirect: to.fullPath } })
  }
  if (isLoggedIn && to.path === '/login') {
    return navigateTo('/')
  }

  // 已登入但 in-memory profile 不見（重整、新 tab），重抓一次
  if (isLoggedIn && !auth.profile) {
    try {
      await auth.fetchProfile()
    }
    catch {
      // 401 / 網路錯 → $api plugin 已 clearSession。middleware 顯式 return navigateTo
      // 中止 target page 繼續 render，避免瀏覽器先閃一下破 UI 才被 async redirect 追上。
      // 帶 `?expired=1` flag → /login 頁 mount 時顯示 toast「登入憑證已過期」
      // （SSR 上 api plugin 的 toast 分支只在 client 端執行，hard reload 場景 client 沒機會 fire）。
      // 同樣帶 redirect，讓重新登入後可以回到原本要去的頁面。
      return navigateTo({ path: '/login', query: { expired: '1', redirect: to.fullPath } })
    }
  }

  // ⚠️ 必須在上面的 fetchProfile 之後：重整 / 開新分頁時 permissions 還沒到手，
  // 提前判斷會讓 hasPermission 一律回 false、把有權限的人也導走。
  //
  // 未登入的情況上面已 return，走到這裡代表 token 存在且 profile 已備妥。
  // 導回 `/`（不設權限需求的安全落點）並帶 denied flag，由該頁 client mount 時
  // 顯示 toast —— middleware 在 SSR 也會跑，不能直接呼叫 vue-sonner。
  //
  // 注意：一律用 to.path 而非 to.fullPath。requiredPermissions() 的比對是
  // 「完全相等或後面接 /」，帶 query 的 fullPath（例如 /upload?x=1）兩者皆不符，
  // 會落到「無權限需求」而直接放行 —— 等於加個 query string 就能繞過守衛。
  const required = requiredPermissions(to.path)
  if (required.length > 0 && !auth.hasPermission(required)) {
    return navigateTo({ path: '/', query: { denied: '1' } })
  }
})
